/**
 * SimulationViewport — React wrapper around the Canvas 2D rocket renderer.
 *
 * Consumes precomputed simulation telemetry and plays it back frame by frame.
 * The rocket position, pitch, and thrust come entirely from the physics
 * telemetry — no decorative animation or fabricated movement.
 *
 * Playback is driven by the parent via currentIndex; this component only
 * renders the frame corresponding to the given index. No local animation
 * loop is used — that prevents duplicate requestAnimationFrame loops.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import type { TelemetryPoint, LandingResult } from "@/lib/physics";
import { makeTransform, computeViewportConfig } from "@/lib/physics/viewport/CoordinateTransform";
import { drawRocket, drawLandingTarget, drawGround } from "@/lib/physics/viewport/RocketRenderer";

interface SimulationViewportProps {
  /** Precomputed simulation telemetry points from the physics engine. */
  telemetry: readonly TelemetryPoint[];
  /** Landing result. */
  landing: LandingResult | null;
  /** Current playback index (0 = start, telemetry.length - 1 = end). */
  currentIndex: number;
  /** Landing target X coordinate (world meters). */
  targetX: number;
  /** Landing zone half-width (world meters). */
  targetZoneHalfWidth: number;
  /** Canvas display height (set by parent). */
  height?: number;
}

export function SimulationViewport({
  telemetry,
  landing,
  currentIndex,
  targetX,
  targetZoneHalfWidth,
  height = 500,
}: SimulationViewportProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transformRef = useRef<ReturnType<typeof makeTransform> | null>(null);
  const [transformVersion, setTransformVersion] = useState(0);

  const recomputeTransform = useCallback(() => {
    const el = canvasRef.current;
    if (!el || telemetry.length === 0) return;
    const config = computeViewportConfig(
      telemetry.map((p) => ({ x: p.x, z: p.z })),
      el.clientWidth,
      height,
      70,
    );
    transformRef.current = makeTransform(config);
    setTransformVersion((v) => v + 1);
  }, [telemetry, height]);

  // Build transform from telemetry bounds.
  useEffect(() => {
    recomputeTransform();
  }, [telemetry, height]);

  // Draw current frame whenever currentIndex changes.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || telemetry.length === 0 || !transformRef.current) return;

    const ctx = el.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = el.getBoundingClientRect();
    if (el.width !== rect.width * dpr || el.height !== rect.height * dpr) {
      el.width = rect.width * dpr;
      el.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    }

    // Clear.
    ctx.clearRect(0, 0, el.width / dpr, el.height / dpr);

    const transform = transformRef.current;
    const worldX = Math.max(
      Math.abs(telemetry[0].x),
      Math.abs(telemetry[telemetry.length - 1].x),
      Math.abs(targetX),
    ) * 1.2;

    // Draw ground and target.
    drawGround(ctx, transform, worldX);
    drawLandingTarget(ctx, transform, targetX, targetZoneHalfWidth);

    // Draw trajectory trail up to current index.
    const endIdx = Math.min(currentIndex, telemetry.length - 1);
    if (endIdx > 0) {
      ctx.beginPath();
      ctx.strokeStyle = "#22d3ee";
      ctx.lineWidth = 1.5 / transform.scale;
      ctx.setLineDash([4, 2]);
      const first = telemetry[0];
      ctx.moveTo(transform.wx(first.x), transform.wz(first.z));
      for (let i = 1; i <= endIdx; i++) {
        const p = telemetry[i];
        ctx.lineTo(transform.wx(p.x), transform.wz(p.z));
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw rocket at current state.
    const current = telemetry[endIdx];
    drawRocket(ctx, transform, current, current.throttle);

    // Draw target crosshair label.
    ctx.fillStyle = "#ef4444";
    ctx.font = "12px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
    ctx.textAlign = "center";
    ctx.fillText("TARGET", transform.wx(targetX), transform.wz(0) - 12 / transform.scale);

    // Draw status badge at top-left of canvas.
    const status = landing?.landingSuccess
      ? "LANDED"
      : currentIndex >= telemetry.length - 1
        ? "IMPACT"
        : "IN FLIGHT";
    ctx.fillStyle = landing?.landingSuccess ? "#10b981" : status === "IMPACT" ? "#ef4444" : "#22d3ee";
    ctx.font = "bold 12px ui-sans-serif, system-ui, -apple-system, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(status, 10 / transform.scale, 20 / transform.scale);
  }, [currentIndex, telemetry, landing, targetX, targetZoneHalfWidth, height, transformVersion]);

  // Resize handler: recompute world-to-screen transform on canvas resize.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const handleResize = () => recomputeTransform();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [recomputeTransform]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      // No local RAF loop to cancel; playback is driven by the parent hook.
    };
  }, []);

  return (
    <div
      className="w-full panel bg-surface border border-border relative overflow-hidden"
      style={{ height }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block"
        style={{ width: "100%", height: "100%" }}
      />
      {/* Empty state overlay when no telemetry. */}
      {telemetry.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted">
          <div className="w-12 h-12 rounded border border-border flex items-center justify-center mb-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 19l7-7 7 7M5 12l7-7 7 7M12 4l-2 8M12 20l-2-8" />
            </svg>
          </div>
          <p className="text-sm">No trajectory data</p>
          <p className="text-[0.625rem] text-dim">Configure and start a simulation to visualize the flight path.</p>
        </div>
      )}
    </div>
  );
}
